import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ujmsDefenseDashboard, ujmsFileAppearance, ujmsWithdrawAppearance,
  ujmsSearchCases, ujmsWorkspace,
} from "../../services/ujmsApi";
import { Panel, Stat, Badge, thCls, tdCls, inputCls, selectCls, Field, btnPrimary, btnGhost, btnDanger, fmtDT, EmptyState } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Shield, UserPlus, ShieldAlert } from "lucide-react";

export default function DefenseWorkspace() {
  const [d, setD] = useState(null);
  const [filing, setFiling] = useState(false);
  const [withdrawing, setWithdrawing] = useState(null);
  const load = useCallback(() => { ujmsDefenseDashboard().then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="defense-workspace">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Defense Workspace</h1>
          <p className="text-sm text-slate-500">Client caseload, appearances and privileged defense work product.</p>
        </div>
        <button className={btnPrimary} onClick={() => setFiling(true)} data-testid="dw-file-appearance-btn">
          <UserPlus className="w-4 h-4" /> File Appearance
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Active Clients" value={d?.active_clients} testId="dw-stat-clients" />
        <Stat label="Hearings (7 days)" value={d?.hearings_7d_count} testId="dw-stat-hearings" />
        <Stat label="Clients In Custody" value={d?.in_custody_count} testId="dw-stat-custody" tone={d?.in_custody_count ? "warn" : undefined} />
        <Stat label="Pending Motions" value={d?.pending_motions_count} testId="dw-stat-motions" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        <div className="xl:col-span-2 space-y-4">
          <Panel title="Client caseload" testId="dw-caseload">
            {(d?.caseload || []).length === 0 ? (
              <EmptyState icon={Shield} title="No active appearances" note="File an appearance on a case to build your caseload." testId="dw-caseload-empty" />
            ) : (
              <table className="w-full border-collapse">
                <thead><tr>
                  <th className={thCls}>Case</th><th className={thCls}>Client</th><th className={thCls}>Charges</th>
                  <th className={thCls}>Next Hearing</th><th className={thCls}>Status</th><th className={thCls}></th>
                </tr></thead>
                <tbody>
                  {(d?.caseload || []).map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50" data-testid={`dw-case-${c.case_number}`}>
                      <td className={`${tdCls} font-mono text-xs`}>
                        <Link className="text-blue-900 hover:underline" to={`/ujms/cases/${c.id}`}>{c.case_number}</Link>
                        <div className="text-slate-400 truncate max-w-[220px]">{c.caption}</div>
                      </td>
                      <td className={tdCls}>
                        {c.client_name}
                        {c.custody_related && <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-mono uppercase bg-red-50 border border-red-200 text-red-700 rounded-sm px-1 py-0.5"><ShieldAlert className="w-3 h-3" /> Custody</span>}
                      </td>
                      <td className={`${tdCls} font-mono text-xs`}>{c.charges?.open}/{c.charges?.total}</td>
                      <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{c.next_hearing ? fmtDT(c.next_hearing.scheduled_start) : "—"}</td>
                      <td className={tdCls}><Badge value={c.status} /></td>
                      <td className={tdCls}>
                        <button className={btnDanger} onClick={() => setWithdrawing(c)} data-testid={`dw-withdraw-${c.case_number}`}>Withdraw</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
          <Panel title="Pending motions on my cases" testId="dw-motions">
            {(d?.pending_motions || []).length === 0 ? (
              <div className="text-sm text-slate-400 p-3">No pending motions.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {(d?.pending_motions || []).map((m) => (
                  <div key={m.id} className="px-3 py-2 text-sm flex items-center justify-between gap-2">
                    <div>
                      <div className="text-slate-800">{m.filing_title || m.title}</div>
                      <div className="text-xs text-slate-400 font-mono">{m.case_number} · {m.filed_by}</div>
                    </div>
                    <Badge value={m.status} />
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
        <div className="space-y-4">
          <Panel title="Upcoming hearings (7 days)" testId="dw-hearings">
            {(d?.upcoming_hearings || []).length === 0 ? (
              <div className="text-sm text-slate-400 p-3">No hearings scheduled.</div>
            ) : (
              <table className="w-full border-collapse"><tbody>
                {(d?.upcoming_hearings || []).map((h) => (
                  <tr key={h.id} className="hover:bg-slate-50">
                    <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(h.scheduled_start)}</td>
                    <td className={tdCls}>
                      <Link className="text-blue-900 hover:underline font-mono text-xs" to={`/ujms/cases/${h.case_id}`}>{h.case_number}</Link>
                      <div className="text-xs text-slate-500">{h.hearing_type_label || h.hearing_type}</div>
                    </td>
                  </tr>
                ))}
              </tbody></table>
            )}
          </Panel>
          <Panel title="Recent orders on my cases" testId="dw-orders">
            {(d?.recent_orders || []).length === 0 ? (
              <div className="text-sm text-slate-400 p-3">No recent orders.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {(d?.recent_orders || []).map((o) => (
                  <div key={o.id} className="px-3 py-2 text-sm flex items-center justify-between gap-2">
                    <div>
                      <div className="text-slate-800">{o.title}</div>
                      <div className="text-xs text-slate-400 font-mono">{o.case_number}</div>
                    </div>
                    <Badge value={o.status} />
                  </div>
                ))}
              </div>
            )}
          </Panel>
          <Panel title="Recent private notes (defense only)" testId="dw-notes">
            {(d?.recent_notes || []).length === 0 ? (
              <div className="text-sm text-slate-400 p-3">No work-product notes yet. Add them from a case's Work Product tab.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {(d?.recent_notes || []).map((n) => (
                  <div key={n.id} className="px-3 py-2 text-sm">
                    <div className="flex items-center gap-2 text-xs text-slate-400"><Badge value={n.category} /><span className="font-mono">{n.case_number}</span></div>
                    <div className="text-slate-700 mt-0.5 line-clamp-2">{n.text}</div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>

      <FileAppearanceDialog open={filing} onClose={() => setFiling(false)} onDone={load} />
      <WithdrawDialog c={withdrawing} onClose={() => setWithdrawing(null)} onDone={load} />
    </div>
  );
}

export function FileAppearanceDialog({ open, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selCase, setSelCase] = useState(null);
  const [parties, setParties] = useState([]);
  const [partyId, setPartyId] = useState("");
  const search = async () => {
    try { const r = await ujmsSearchCases({ q, page_size: 8 }); setResults(r.data.items); } catch (e) { /* non-blocking */ }
  };
  const pick = async (c) => {
    setSelCase(c); setPartyId("");
    try { const r = await ujmsWorkspace(c.id); setParties(r.data.parties); } catch { setParties([]); }
  };
  const submit = async () => {
    try {
      await ujmsFileAppearance(selCase.id, { party_id: partyId });
      toast.success(`APPEARANCE_FILED — ${selCase.case_number}`);
      onClose(); onDone(); setSelCase(null); setResults([]); setQ("");
    } catch (e) { toast.error(e.response?.data?.detail || "Filing failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dw-appearance-dialog">
        <DialogHeader><DialogTitle>File appearance</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Find case" required>
            <div className="flex gap-2">
              <input className={inputCls} value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Case number or party…" data-testid="dw-appearance-search"
                onKeyDown={(e) => e.key === "Enter" && search()} />
              <button className={btnGhost} onClick={search} data-testid="dw-appearance-search-btn">Search</button>
            </div>
          </Field>
          {!selCase && results.length > 0 && (
            <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-48 overflow-y-auto">
              {results.map((c) => (
                <button key={c.id} onClick={() => pick(c)} data-testid={`dw-appearance-case-${c.case_number}`}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
                  <span className="font-mono text-xs text-blue-900">{c.case_number}</span>
                  <span className="text-slate-600 ml-2">{c.caption}</span>
                </button>
              ))}
            </div>
          )}
          {selCase && (
            <>
              <div className="text-sm bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                <span className="font-mono text-xs text-blue-900">{selCase.case_number}</span> — {selCase.caption}
                <button className="ml-2 text-xs text-slate-400 underline" onClick={() => setSelCase(null)}>change</button>
              </div>
              <Field label="On behalf of (party)" required>
                <select className={selectCls} value={partyId} onChange={(e) => setPartyId(e.target.value)} data-testid="dw-appearance-party">
                  <option value="">Select party…</option>
                  {parties.map((p) => (
                    <option key={p.id} value={p.id}>{p.display_name} ({p.relationship_type}){p.attorney_name ? ` — current counsel: ${p.attorney_name}` : ""}</option>
                  ))}
                </select>
              </Field>
            </>
          )}
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={!selCase || !partyId} data-testid="dw-appearance-submit">File Appearance</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function WithdrawDialog({ c, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const submit = async () => {
    try {
      await ujmsWithdrawAppearance(c.appearance_id, reason);
      toast.success("APPEARANCE_WITHDRAWN — docketed");
      onClose(); onDone(); setReason("");
    } catch (e) { toast.error(e.response?.data?.detail || "Withdrawal failed"); }
  };
  return (
    <Dialog open={!!c} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="dw-withdraw-dialog">
        <DialogHeader><DialogTitle>Withdraw appearance — {c?.case_number}</DialogTitle></DialogHeader>
        <Field label="Reason (docketed)" required>
          <input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Substitution of counsel…" data-testid="dw-withdraw-reason" />
        </Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnDanger} onClick={submit} disabled={!reason.trim()} data-testid="dw-withdraw-submit">Withdraw</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
